import { create } from "zustand";
import type { AgentStatus, EditEvent } from "../types/ai";
import { normalizeEditEvent } from "../types/ai";
import { api } from "../lib/api";
import { useAIStore } from "./ai";
import { useAgentChatStore } from "./agentChat";

interface InlineAssistantMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

interface InlineAssistantState {
  isOpen: boolean;
  messages: InlineAssistantMessage[];
  agentStatus: AgentStatus;
  streamingContent: string;
  lastResponse: string;
  pendingProposal: {
    proposedContent: string;
    description: string;
  } | null;
  conversationId: string | null;
  _abortController: AbortController | null;

  toggle: () => void;
  open: () => void;
  close: () => void;
  cancelStream: () => void;
  clearResponse: () => void;
  dismissProposal: () => void;
  startNewThread: () => void;

  sendMessage: (params: {
    message: string;
    documentId?: string;
    projectId?: string;
    referencedFileIds?: string[];
    cursorLine?: number;
    selectedText?: string;
    selectionStart?: number;
    selectionEnd?: number;
    onEdit?: (edit: EditEvent) => void;
  }) => Promise<void>;
}

export const useInlineAssistantStore = create<InlineAssistantState>()(
  (set, get) => ({
    isOpen: false,
    messages: [],
    agentStatus: "idle",
    streamingContent: "",
    lastResponse: "",
    pendingProposal: null,
    conversationId: null,
    _abortController: null,

    toggle: () => {
      const { isOpen } = get();
      if (isOpen) {
        get().cancelStream();
      }
      set({ isOpen: !isOpen });
    },

    open: () => set({ isOpen: true }),

    close: () => {
      get().cancelStream();
      set({ isOpen: false });
    },

    cancelStream: () => {
      const { _abortController } = get();
      if (_abortController) {
        _abortController.abort();
        set({
          _abortController: null,
          agentStatus: "idle",
          streamingContent: "",
        });
      }
    },

    clearResponse: () => set({ lastResponse: "", pendingProposal: null }),

    dismissProposal: () => set({ pendingProposal: null }),

    startNewThread: () => {
      get().cancelStream();
      set({
        conversationId: null,
        messages: [],
        lastResponse: "",
        pendingProposal: null,
        streamingContent: "",
      });
    },

    sendMessage: async (params) => {
      get().cancelStream();
      let { conversationId } = get();

      const userMessage: InlineAssistantMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: params.message,
        timestamp: Date.now(),
      };

      set((s) => ({
        messages: [...s.messages, userMessage],
        agentStatus: "thinking",
        streamingContent: "",
        lastResponse: "",
        pendingProposal: null,
      }));

      const abortController = new AbortController();
      set({ _abortController: abortController });

      let receivedDone = false;
      let fullContent = "";

      try {
        const { selectedProvider, selectedModel } = useAIStore.getState();
        const payload = {
          conversation_id: conversationId || undefined,
          document_id: params.documentId,
          project_id: params.projectId,
          referenced_file_ids: params.referencedFileIds,
          message: params.message,
          provider: selectedProvider || undefined,
          model: selectedModel || undefined,
          cursor_line: params.cursorLine,
          selection_start: params.selectionStart,
          selection_end: params.selectionEnd,
          selected_text: params.selectedText,
        };

        let lastToolCallId: string | undefined;

        for await (const data of api.stream(
          "/api/v1/ai/chat",
          payload,
          abortController.signal,
        )) {
          try {
            const event = JSON.parse(data);

            if (event.event === "conversation" && event.data?.id) {
              set({ conversationId: event.data.id as string });
            }

            if (event.event === "status") {
              const statusMap: Record<string, AgentStatus> = {
                thinking: "thinking",
                reading: "reading",
                compiling: "compiling",
                editing: "editing",
              };
              set({
                agentStatus: statusMap[event.data?.status] || "thinking",
              });
            }

            if (event.event === "text") {
              fullContent += event.data?.content || "";
              set({ streamingContent: fullContent });
            }

            if (event.event === "tool_call") {
              lastToolCallId = event.data?.id as string | undefined;
              const toolStatusMap: Record<string, AgentStatus> = {
                compile_latex: "compiling",
                edit_document: "editing",
                edit_lines: "editing",
                read_document: "reading",
                read_section: "reading",
                search_document: "reading",
              };
              set({
                agentStatus:
                  toolStatusMap[event.data?.tool] || "thinking",
              });
            }

            if (event.event === "edit") {
              const edit = normalizeEditEvent(
                event.data as Record<string, unknown>,
                lastToolCallId,
              );
              const proposed =
                edit.full_new_content ?? edit.newContent ?? edit.content;
              if (proposed) {
                set({
                  pendingProposal: {
                    proposedContent: proposed,
                    description: params.message.slice(0, 60),
                  },
                });
              }
              params.onEdit?.(edit);
            }

            if (event.event === "done") {
              receivedDone = true;
              set({ agentStatus: "done" });
              setTimeout(() => {
                set((s) =>
                  s.agentStatus === "done" ? { agentStatus: "idle" } : {},
                );
              }, 2000);
            }
          } catch {
            // Skip malformed JSON chunks
          }
        }

        if (!receivedDone) {
          set({ agentStatus: "idle" });
        }

        const responseText = fullContent.trim()
          ? fullContent
          : !receivedDone
            ? "No response received. Try again."
            : "";

        const assistantMessage: InlineAssistantMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: responseText,
          timestamp: Date.now(),
        };

        set((s) => ({
          messages: [...s.messages, assistantMessage],
          streamingContent: "",
          lastResponse: responseText,
          _abortController: null,
        }));

        if (params.documentId) {
          useAgentChatStore.getState()
            .refreshConversationsForDocument(params.documentId, params.projectId ?? null)
            .catch(() => {});
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          const partialText = fullContent.trim();
          if (partialText) {
            const partialMessage: InlineAssistantMessage = {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              content: partialText,
              timestamp: Date.now(),
            };
            set((s) => ({
              _abortController: null,
              agentStatus: "idle",
              streamingContent: "",
              lastResponse: partialText,
              messages: [...s.messages, partialMessage],
            }));
          } else {
            set({
              _abortController: null,
              agentStatus: "idle",
              streamingContent: "",
            });
          }
          return;
        }
        const errorMsg = "Something went wrong. Please try again.";
        const errorMessage: InlineAssistantMessage = {
          id: `assistant-error-${Date.now()}`,
          role: "assistant",
          content: errorMsg,
          timestamp: Date.now(),
        };
        set((s) => ({
          agentStatus: "idle",
          streamingContent: "",
          _abortController: null,
          lastResponse: errorMsg,
          messages: [...s.messages, errorMessage],
        }));
      }
    },
  }),
);
